import React, { useState } from 'react';

import { AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { IconShare } from '@pkg/assets/icons';
import { InfoAction, RhythmScoreBadge, RhythmScoreModal, Sheet, YatShareModule } from '@pkg/components';
import { CartButton } from '@pkg/components/Navigation/TopNav/parts';
import { useAppSelector, useBreakpoints } from '@pkg/hooks';

import { useEmojisStore } from '@app/store/zustand/emojis/useEmojisStore';

import { useIsPreview } from '../../hooks/useIsPreview';

import { LiquidId, UserBio, UserBioStatic, UserName, UserNameStatic, YatStatic } from './parts';
import { useEmojiDetailsQuery } from './useEmojiDetailsQuery';
// import { FantasyRank } from './unused';

import {
    ActionIcon,
    ActionLabel,
    ContentHolder,
    LeftSide,
    LiquidIdWrapper,
    RhythmWrapper,
    RightSide,
    SecondaryAction,
    ShareAction,
    TempShareWrapper,
    Wrapper,
    YatRhythm,
} from './ProfileHeader.style';

export function ProfileHeader() {
    const [translate] = useTranslation(['common', 'dashboard']);
    const { isMobile } = useBreakpoints();
    const location = useLocation();

    const isPageOwner = useAppSelector((state) => state.yatPage.isPageOwner);
    const username = useAppSelector((state) => state.yatPage.heading);
    const bio = useAppSelector((state) => state.yatPage.bio);
    const eid = useAppSelector((state) => state.yatPage.eid);

    const setCurrentUserEmoji = useEmojisStore((state) => state.setCurrentEmoji);
    const { generation, score, modalContent } = useEmojiDetailsQuery();
    const [modalShow, setModalShow] = useState(false);

    function handleModalClose() {
        setModalShow(false);
    }

    function handleModalShow() {
        setModalShow(true);
    }

    function handleShareClick() {
        if (eid) setCurrentUserEmoji(eid);
    }

    const isPreview = useIsPreview();

    const editMode = Boolean(isPageOwner && !isPreview);

    const userNameMarkup = editMode ? <UserName initialValue={username} /> : <UserNameStatic value={username} />;

    const userBioFallback = bio?.length ? <UserBioStatic value={bio} /> : null;
    const userBioMarkup = editMode ? <UserBio initialValue={bio} /> : userBioFallback;

    const exitActionMarkup =
        isPageOwner && isPreview ? (
            <SecondaryAction url={location.pathname}>
                <ActionLabel>Exit preview</ActionLabel>
            </SecondaryAction>
        ) : null;

    const previewActionMarkup = editMode ? (
        <SecondaryAction url="?preview=1">
            <ActionLabel>Preview public profile</ActionLabel>
        </SecondaryAction>
    ) : null;

    // TODO: `YatShareModule > customButton` does a strange thing
    // where we pass a <button /> as a child to a wrapping <button />
    //  with its own `onClick`... but then we also wrap the whole
    // `YatShareModule` in a <div /> that has another `onClick`...
    // this needs to be revised so we are not nesting `onClick` callbacks.
    const shareActionMarkup = (
        <ShareAction>
            <ActionIcon>
                <IconShare />
            </ActionIcon>
            <ActionLabel>Share</ActionLabel>
        </ShareAction>
    );

    const rhythmModalAction = modalContent.description ? (
        <InfoAction ariaLabel={translate('dashboard:tell_me_more')} onAction={handleModalShow} />
    ) : null;

    const rhythmModalMarkup = modalShow ? (
        <Sheet size="thin" gapless backdrop onClose={handleModalClose}>
            <Sheet.Header title={translate('common:rhythm_score')} largeTitle onClose={handleModalClose} />

            <Sheet.Main>
                <Sheet.Layout>
                    <Sheet.Layout.Columns stretch>
                        <Sheet.Layout.Columns.Primary padded scrollable>
                            <RhythmScoreModal
                                hideTitle
                                generation={generation}
                                rhythmScore={score}
                                rhythmScoreCopy={modalContent}
                            />
                        </Sheet.Layout.Columns.Primary>
                    </Sheet.Layout.Columns>
                </Sheet.Layout>
            </Sheet.Main>
        </Sheet>
    ) : null;

    return (
        <Wrapper>
            {isMobile && <CartButton />}

            <LeftSide>
                <LiquidIdWrapper>
                    <LiquidId />
                </LiquidIdWrapper>

                <ContentHolder>
                    <YatRhythm>
                        <YatStatic />

                        {score ? (
                            <RhythmWrapper>
                                <RhythmScoreBadge generation={generation} score={score} badgeStyle="bare" />

                                {rhythmModalAction}
                            </RhythmWrapper>
                        ) : null}
                    </YatRhythm>

                    {userNameMarkup}
                    {userBioMarkup}
                    {/* marketplaceV1Enabled ? <FantasyRank /> : null */}
                </ContentHolder>
            </LeftSide>

            <RightSide>
                {exitActionMarkup}
                {previewActionMarkup}

                <TempShareWrapper onClick={handleShareClick}>
                    <YatShareModule directionDown directionLeft emojiId={eid ?? ''} customButton={shareActionMarkup} />
                </TempShareWrapper>
            </RightSide>

            <AnimatePresence>{rhythmModalMarkup}</AnimatePresence>
        </Wrapper>
    );
}
